import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColleaguePage, ConversationSummary } from '@up2d8/contracts';
import { Avatar, Button, Dialog, Input, cn } from '@up2d8/ui';
import { useSession } from '../../../shared/session';

/**
 * Het venster achter de knop "nieuw bericht" (spec §8: niet vormgegeven).
 *
 * De spec tekent deze knop wel en het venster erachter niet. Ontworpen in dezelfde stijl, en
 * bewust klein gehouden: dit is geen apart scherm maar een keuze van één of meer mensen.
 *
 * Twee wegen in één venster, want de vraag "met wie" is dezelfde:
 *
 * - **een collega aanspreken** — klik op een naam en het gesprek staat er (idempotent: bestaat het
 *   al, dan open je het bestaande);
 * - **een groep beginnen** — vink meerdere mensen aan, geef de groep een naam.
 *
 * Welke van de twee het wordt, blijkt uit wat je doet en niet uit een keuzeknop vooraf. Wie één
 * persoon aanklikt, hoeft niet eerst te zeggen dat hij geen groep wil.
 */
export function DeskNewConversation({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { api } = useSession();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [title, setTitle] = useState('');
  const [failed, setFailed] = useState(false);

  const colleagues = useQuery({
    queryKey: ['colleagues', 'new-conversation', search],
    enabled: open,
    queryFn: async (): Promise<ColleaguePage> => {
      const { data, error } = await api.GET('/api/v1/colleagues', {
        params: { query: search.trim() ? { q: search.trim() } : {} },
      });
      if (error || !data) throw error ?? new Error('Ophalen mislukt');
      return data as ColleaguePage;
    },
  });

  const close = () => {
    setSearch('');
    setSelected([]);
    setTitle('');
    setFailed(false);
    onClose();
  };

  const start = useMutation({
    mutationFn: async (): Promise<ConversationSummary> => {
      const body =
        selected.length === 1
          ? { personId: selected[0] as string }
          : { title: title.trim(), participantIds: selected };
      const { data, error } = await api.POST('/api/v1/conversations', { body: body as never });
      if (error || !data) throw error ?? new Error('Starten mislukt');
      return data as ConversationSummary;
    },
    onSuccess: (conversation) => {
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      close();
      void navigate({
        to: '/app/gesprekken/$conversationId',
        params: { conversationId: conversation.id },
      });
    },
    onError: () => setFailed(true),
  });

  const people = colleagues.data?.items ?? [];
  const isGroup = selected.length > 1;
  // Eén persoon vraagt geen naam; een groep wel, want twee groepen met dezelfde leden zijn pas uit
  // elkaar te houden door hun naam (ADR-0067).
  const canStart = selected.length === 1 || (isGroup && title.trim().length > 0);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) close();
      }}
      title={t('messaging.newConversation')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={() => start.mutate()}
            disabled={!canStart || start.isPending}
            data-testid="new-conversation-start"
          >
            {isGroup ? t('messaging.createGroup') : t('messaging.startConversation')}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-element">
        <Input
          label={t('messaging.newSearchLabel')}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t('messaging.newSearchPlaceholder')}
          data-testid="new-conversation-search"
        />

        <ul className="flex max-h-64 flex-col overflow-y-auto" data-testid="new-conversation-list">
          {colleagues.isPending ? (
            <li className="py-3 text-small text-text-muted">{t('common.loading')}</li>
          ) : people.length === 0 ? (
            <li className="py-3 text-small text-text-muted">{t('colleagues.empty')}</li>
          ) : (
            people.map((person) => {
              const name = person.displayName;
              const picked = selected.includes(person.personId);
              return (
                <li key={person.personId}>
                  <button
                    type="button"
                    data-testid="new-conversation-person"
                    aria-pressed={picked}
                    onClick={() =>
                      setSelected((current) =>
                        current.includes(person.personId)
                          ? current.filter((id) => id !== person.personId)
                          : [...current, person.personId],
                      )
                    }
                    className={cn(
                      'flex min-h-touch w-full items-center gap-element rounded-md px-2 py-2 text-left transition-colors',
                      picked ? 'bg-brand-primary-tint' : 'hover:bg-background',
                    )}
                  >
                    <Avatar name={name} size="md" />
                    <span className="min-w-0 flex-1 truncate text-content text-text-primary">
                      {name}
                    </span>
                  </button>
                </li>
              );
            })
          )}
        </ul>

        {/* De naam verschijnt pas zodra het een groep wordt. Een leeg naamveld boven een lijst
            waarin je nog niemand koos, is een vraag die nog niet gesteld hoort te worden. */}
        {isGroup && (
          <Input
            label={t('messaging.groupNameLabel')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t('messaging.groupNamePlaceholder')}
            data-testid="new-conversation-title"
          />
        )}

        {failed && (
          <p role="alert" className="text-small text-danger">
            {t('messaging.startFailed')}
          </p>
        )}
      </div>
    </Dialog>
  );
}
